import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatFCFA, statusLabel, universeLabel } from "@/lib/api";
import { TrendingUp, ShoppingCart, Package, AlertCircle } from "lucide-react";
import { useAuth } from "@/lib/auth";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const COLORS = ["#0A2E1F", "#E65C00", "#6E0D25"];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const { hasPerm, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Manager without stats.view → redirect to their primary scope (orders)
    if (!isAdmin && !hasPerm("stats.view")) {
      if (hasPerm("orders.view")) navigate("/admin/commandes", { replace: true });
      return;
    }
    Promise.all([api.get("/admin/stats"), api.get("/admin/orders")]).then(([s, o]) => {
      setStats(s.data);
      const list = Array.isArray(o.data) ? o.data : (o.data?.items || []);
      setOrders(list.slice(0, 5));
    });
  }, []);

  if (!stats) return <div className="text-[var(--dsf-text-muted)]">Chargement…</div>;

  const kpis = [
    { label: "Chiffre d'affaires", value: formatFCFA(stats.total_revenue), icon: TrendingUp, accent: "var(--dsf-orange)" },
    { label: "Commandes totales", value: stats.total_orders, icon: ShoppingCart, accent: "var(--dsf-green)" },
    { label: "En cours", value: stats.pending_orders, icon: Package, accent: "var(--dsf-burgundy)" },
    { label: "Stocks faibles", value: stats.low_stock_count, icon: AlertCircle, accent: "var(--dsf-orange)" },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="font-serif text-4xl text-[var(--dsf-green)]">Vue d'ensemble</h1>
        <p className="text-[var(--dsf-text-muted)] text-sm mt-1">Performance commerciale et opérationnelle, en temps réel.</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map((k, i) => (
          <div key={i} className="bg-white border border-[var(--dsf-border)] p-6 hover:-translate-y-1 transition-transform" data-testid={`kpi-${i}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-text-muted)]">{k.label}</div>
              <k.icon size={18} style={{ color: k.accent }} />
            </div>
            <div className="font-serif text-3xl font-semibold text-[var(--dsf-green)]">{k.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue chart */}
        <div className="lg:col-span-2 bg-white border border-[var(--dsf-border)] p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-burgundy)]">7 derniers jours</div>
              <h2 className="font-serif text-2xl text-[var(--dsf-green)]">Revenus journaliers</h2>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.revenue_by_day}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8E5" />
                <XAxis dataKey="day" stroke="#82938B" fontSize={12} />
                <YAxis stroke="#82938B" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: "#0A2E1F", border: "none", color: "white", borderRadius: 0 }}
                  formatter={(v) => formatFCFA(v)}
                />
                <Bar dataKey="revenue" fill="#E65C00" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Universe split */}
        <div className="bg-white border border-[var(--dsf-border)] p-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-burgundy)]">Répartition</div>
          <h2 className="font-serif text-2xl text-[var(--dsf-green)] mb-4">Par univers</h2>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={stats.universe_revenue} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80}>
                  {stats.universe_revenue.map((_, i) => (
                    <Cell key={i} fill={COLORS[i]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => formatFCFA(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top products */}
        <div className="bg-white border border-[var(--dsf-border)] p-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-burgundy)]">Best-sellers</div>
          <h2 className="font-serif text-2xl text-[var(--dsf-green)] mb-5">Top produits</h2>
          <div className="space-y-3">
            {stats.top_products.map((p, i) => (
              <div key={i} className="flex items-center gap-4">
                <div className="w-7 h-7 bg-[var(--dsf-green)] text-white flex items-center justify-center text-xs font-semibold">
                  {i + 1}
                </div>
                <div className="flex-1">
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-[var(--dsf-text-muted)]">{p.sold} vendus</div>
                </div>
              </div>
            ))}
            {stats.top_products.length === 0 && (
              <div className="text-sm text-[var(--dsf-text-muted)]">Aucune vente enregistrée pour le moment.</div>
            )}
          </div>
        </div>

        {/* Recent orders */}
        <div className="bg-white border border-[var(--dsf-border)] p-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-burgundy)]">Activité récente</div>
          <h2 className="font-serif text-2xl text-[var(--dsf-green)] mb-5">Dernières commandes</h2>
          <div className="space-y-3">
            {orders.map((o) => (
              <div key={o.id} className="flex items-center justify-between text-sm border-b border-[var(--dsf-border)] pb-3 last:border-b-0">
                <div>
                  <div className="font-semibold">{o.order_number}</div>
                  <div className="text-xs text-[var(--dsf-text-muted)]">{o.customer_name}</div>
                </div>
                <span className={`status-pill status-${o.status}`}>{statusLabel(o.status)}</span>
                <div className="font-medium">{formatFCFA(o.total)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
